import React, { useEffect, Suspense } from "react";
import {
    graphql,
    PreloadedQuery,
    useMutation,
    usePreloadedQuery,
    useQueryLoader,
} from "react-relay";
import Alert from "@mui/material/Alert";
import IconButton from "@mui/material/IconButton";
import CampaignOutlinedIcon from "@mui/icons-material/CampaignOutlined";
import CloseIcon from "@mui/icons-material/Close";
import PropTypes from "prop-types";

import { HubNoteQuery as HubNoteQueryType } from "__generated__/HubNoteQuery.graphql";
import { HubNoteDismissMutation as HubNoteDismissMutationType } from "__generated__/HubNoteDismissMutation.graphql";

import ErrorContainer from "apps/components/ErrorContainer";
import { TranslationFn } from "lib/types";

const HubNoteQuery = graphql`
    query HubNoteQuery {
        me {
            id
            hubNoteDismissed
        }
        nextSurvey {
            id
            displayTitle
            linkUrl
        }
    }
`;

const HubNoteDismissMutation = graphql`
    mutation HubNoteDismissMutation {
        dismissHubNote {
            me {
                id
                hubNoteDismissed
            }
        }
    }
`;
type Props = {
    fallback?: React.ReactNode;
    hideDismiss?: boolean;
};

type ContentProps = Props & {
    queryRef: PreloadedQuery<HubNoteQueryType>;
};

const HubNoteContent = (
    { queryRef, fallback, hideDismiss = false }: ContentProps,
    { t: _ }: { t: TranslationFn },
) => {
    const { me, nextSurvey } = usePreloadedQuery(HubNoteQuery, queryRef);
    const [dismissHubNote] = useMutation<HubNoteDismissMutationType>(HubNoteDismissMutation);

    if (!nextSurvey || me.hubNoteDismissed) return fallback;

    const onDismiss = () => {
        dismissHubNote({
            variables: {},
            optimisticResponse: {
                dismissHubNote: { id: me.id, hubNoteDismissed: true },
            },
        });
    };

    return (
        <Alert severity="info" icon={false} className="hubnote__alert">
            <div className="hubnote__alert-inner">
                <CampaignOutlinedIcon fontSize="small" />
                <span>
                    {_(`Share your thoughts with the team. Go to ${nextSurvey.displayTitle}: `)}
                    <a className="indigo-text-link" href={nextSurvey.linkUrl}>
                        {nextSurvey.linkUrl}
                    </a>
                </span>
                {!hideDismiss && (
                    <IconButton size="small" aria-label="close" onClick={onDismiss}>
                        <CloseIcon fontSize="small" />
                    </IconButton>
                )}
            </div>
        </Alert>
    );
};

HubNoteContent.contextTypes = {
    t: PropTypes.func,
};

const HubNoteWrapper = ({ fallback, ...props }: Props) => {
    const [queryRef, loadQuery, disposeQuery] = useQueryLoader<HubNoteQueryType>(HubNoteQuery);

    useEffect(() => {
        loadQuery({});
        return () => {
            disposeQuery();
        };
    }, [loadQuery, disposeQuery]);

    if (!queryRef) return null;

    return <HubNoteContent queryRef={queryRef} fallback={fallback} {...props} />;
};

const HubNote = ({ fallback = null, ...props }: Props) => (
    <ErrorContainer hideContainer fallback={null}>
        <Suspense fallback={null}>
            <HubNoteWrapper fallback={fallback} {...props} />
        </Suspense>
    </ErrorContainer>
);

export default HubNote;
